import React, { useState, useEffect } from 'react';
import { Form, Button, Row, Col, Spinner, Card } from 'react-bootstrap';
import { useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import moment from 'moment';
import { Get, Post, Put } from '../../../../utils/httpProvider';
import { CONGNHANTIENSI_API, BaseGatewayImage } from '../../../../utils/apiUrl';
import { trangThaiGuiKetQuaValue } from '../../../../utils/define';
import DocumentInput from '../../../../components/document-input/DocumentInput';
import { ConfirmModal } from '../../../../modules/modal/ConfirmModal';

const FieldDisplayFile = ({ label, value }) => {
    const getSafeUrl = (path) => {
        if (!path) return null;
        if (path.startsWith('http')) return path;
        const baseUrl = BaseGatewayImage.endsWith('/') ? BaseGatewayImage.slice(0, -1) : BaseGatewayImage;
        const safePath = path.startsWith('/') ? path : `/${path}`;
        return `${baseUrl}${safePath}`;
    };

    let files = [];
    if (value) {
        try {
            const parsed = JSON.parse(value);
            if (Array.isArray(parsed)) {
                files = parsed;
            } else {
                files = [{ filePath: value, name: value.split('/').pop() }];
            }
        } catch (e) {
            files = [{ filePath: value, name: value.split('/').pop() }];
        }
    }

    return (
        <Form.Group className="mb-3">
            {label && <Form.Label className="font-weight-bold">{label}</Form.Label>}
            <div className="d-flex flex-wrap">
                {files.length > 0 ? (
                    files.map((file, idx) => {
                        const path = file.filePath || file;
                        let name = file.name || (typeof path === 'string' ? path.split('/').pop() : 'File');
                        name = name.replace(/_\d{14}(?=\.\w+$|$)/, '');
                        return (
                            <a key={idx} href={getSafeUrl(path)} target="_blank" rel="noreferrer" className={`btn btn-outline-primary text-left ${label ? '' : 'btn-sm'} mb-1 mr-1`} style={{ maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block' }} title={name}>
                                <i className="fas fa-paperclip mr-1"></i> {name}
                            </a>
                        );
                    })
                ) : (
                    <span className="text-muted small">Không có file</span>
                )}
            </div>
        </Form.Group>
    );
};

const FieldDisplay = ({ label, value }) => {
    return (
        <Form.Group className="mb-3">
            {label && <Form.Label className="font-weight-bold">{label}</Form.Label>}
            <div>
                <span className="text-dark" style={{ fontSize: '15px', fontWeight: 600 }}>
                    {value !== null && value !== undefined && value !== '' ? value : <span className="text-muted font-italic" style={{ fontWeight: 'normal' }}>Chưa cập nhật</span>}
                </span>
            </div>
        </Form.Group>
    );
};

const CongNhanTienSiForm = ({ hoSoId, readOnly = false, isUserView = false, onDataLoaded, onSaveSuccess }) => {
    const queryClient = useQueryClient();
    const [saving, setSaving] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    
    const [congNhanTienSiId, setCongNhanTienSiId] = useState(null);
    const [soQuyetDinh, setSoQuyetDinh] = useState('');
    const [ngayQuyetDinh, setNgayQuyetDinh] = useState('');
    const [soBang, setSoBang] = useState('');
    const [fileQuyetDinh, setFileQuyetDinh] = useState('');
    const [trangThaiGuiKetQua, setTrangThaiGuiKetQua] = useState(trangThaiGuiKetQuaValue.chuaGui);

    const { data: queryRes, refetch, isLoading } = useQuery(
        ['congNhanTienSiData', hoSoId], 
        () => Get(`${CONGNHANTIENSI_API.GET_BY_HOSO_ID}/${hoSoId}`),
        { 
            enabled: !!hoSoId,
            refetchOnWindowFocus: false 
        }
    );

    const parseTrangThaiGuiKetQua = (val) => {
        if (typeof val === 'number') return val;
        if (val === 'DaGui') return trangThaiGuiKetQuaValue.daGui;
        return trangThaiGuiKetQuaValue.chuaGui;
    };

    useEffect(() => {
        if (queryRes) {
            let item = null;
            if (Array.isArray(queryRes)) {
                item = queryRes[0];
            } else if (queryRes.data && Array.isArray(queryRes.data)) {
                item = queryRes.data[0];
            } else if (queryRes.data?.$values) {
                item = queryRes.data.$values[0];
            } else if (queryRes.data) {
                item = queryRes.data;
            } else {
                item = queryRes;
            }

            if (item && item.id) {
                setCongNhanTienSiId(item.id);
                setSoQuyetDinh(item.soQuyetDinh || '');
                setNgayQuyetDinh(item.ngayQuyetDinh ? moment(item.ngayQuyetDinh).format('YYYY-MM-DD') : '');
                setSoBang(item.soBang || '');
                setFileQuyetDinh(item.fileQuyetDinh || '');
                setTrangThaiGuiKetQua(parseTrangThaiGuiKetQua(item.trangThaiGuiKetQua));
                
                if (onDataLoaded) onDataLoaded(true);
            } else {
                if (onDataLoaded) onDataLoaded(false);
            }
        } else if (queryRes === null) {
            if (onDataLoaded) onDataLoaded(false);
        }
    }, [queryRes]);

    const handleSave = async (isSend = false) => {
        setSaving(true);
        try {
            const payload = {
                HoSoNghienCuuSinhId: hoSoId,
                SoQuyetDinh: soQuyetDinh || '',
                NgayQuyetDinh: ngayQuyetDinh || null,
                SoBang: soBang || '',
                FileQuyetDinh: fileQuyetDinh || '',
                TrangThaiGuiKetQua: isSend ? trangThaiGuiKetQuaValue.daGui : trangThaiGuiKetQua
            };

            if (congNhanTienSiId) {
                await Put(CONGNHANTIENSI_API.UPDATE_ITEM, congNhanTienSiId, { ...payload, Id: congNhanTienSiId });
            } else {
                await Post(CONGNHANTIENSI_API.CREATE, null, payload);
            }

            toast.success(isSend ? 'Gửi kết quả thành công!' : 'Lưu thông tin thành công!');
            await refetch();
            queryClient.invalidateQueries(['congNhanTienSiUser']);
            if (onSaveSuccess) onSaveSuccess();
        } catch (error) {
            console.error(error);
            toast.error('Có lỗi xảy ra khi lưu kết quả.');
        } finally {
            setSaving(false);
            setShowConfirm(false);
        }
    };

    const handleOpenConfirm = () => {
        setShowConfirm(true);
    };

    if (isLoading) return <div className="text-center p-3"><Spinner animation="border" variant="primary" /></div>;

    const isDaGui = trangThaiGuiKetQua === trangThaiGuiKetQuaValue.daGui;

    if (isUserView && !isDaGui) {
        return (
            <div className="text-center p-5 text-muted">
                <i className="fas fa-file-alt fa-3x mb-3 text-secondary"></i>
                <h5>Chưa có quyết định công nhận tiến sĩ</h5>
                <p>Thông tin công nhận tiến sĩ sẽ hiển thị ở đây khi được cập nhật và gửi tới bạn.</p>
            </div>
        );
    }

    return (
        <div className="bg-white p-3 rounded">
            <Card className="mb-3 border-info shadow-sm">
                <Card.Header className="bg-info text-white font-weight-bold">Quyết định công nhận tiến sĩ</Card.Header>
                <Card.Body>
                    <Row>
                        <Col md={6} className="mb-3">
                            {readOnly || isUserView ? (
                                <FieldDisplay label="Số quyết định" value={soQuyetDinh} />
                            ) : (
                                <Form.Group>
                                    <Form.Label className="font-weight-bold">Số quyết định</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={soQuyetDinh}
                                        onChange={(e) => setSoQuyetDinh(e.target.value)}
                                        placeholder="Nhập số quyết định..."
                                    />
                                </Form.Group>
                            )}
                        </Col>

                        <Col md={6} className="mb-3">
                            {readOnly || isUserView ? (
                                <FieldDisplay label="Ngày quyết định" value={ngayQuyetDinh ? moment(ngayQuyetDinh).format('DD/MM/YYYY') : ''} />
                            ) : (
                                <Form.Group>
                                    <Form.Label className="font-weight-bold">Ngày quyết định</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={ngayQuyetDinh}
                                        onChange={(e) => setNgayQuyetDinh(e.target.value)}
                                    />
                                </Form.Group>
                            )}
                        </Col>

                        <Col md={12} className="mb-3">
                            {readOnly || isUserView ? (
                                <FieldDisplay label="Số bằng" value={soBang} />
                            ) : (
                                <Form.Group>
                                    <Form.Label className="font-weight-bold">Số bằng</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={soBang}
                                        onChange={(e) => setSoBang(e.target.value)}
                                        placeholder="Nhập số bằng..."
                                    />
                                </Form.Group>
                            )}
                        </Col>
                        
                        <Col md={12}>
                            {readOnly || isUserView ? (
                                <FieldDisplayFile label="File quyết định công nhận tiến sĩ" value={fileQuyetDinh} />
                            ) : (
                                <Form.Group>
                                    <DocumentInput
                                        labelTitle="File quyết định công nhận tiến sĩ"
                                        value={fileQuyetDinh}
                                        onChangeProp={(url) => setFileQuyetDinh(url)}
                                        readOnly={readOnly || isUserView}
                                    />
                                </Form.Group>
                            )}
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {!readOnly && !isUserView && (
                <div className="text-right mt-4">
                    <Button variant="success" onClick={() => handleSave(false)} disabled={saving}>
                        {saving ? <Spinner size="sm" animation="border" className="mr-1" /> : <i className="fas fa-save mr-1"></i>}
                        Lưu thông tin
                    </Button>
                    <Button variant="primary" className="ml-2" onClick={handleOpenConfirm} disabled={saving}>
                        {saving ? <><Spinner size="sm" animation="border" className="mr-2" /> Đang gửi...</> : <><i className="fas fa-paper-plane mr-2"></i> {isDaGui ? 'Gửi lại kết quả' : 'Gửi kết quả'}</>}
                    </Button>
                </div>
            )}

            <ConfirmModal
                isOpen={showConfirm}
                title="Xác nhận gửi kết quả"
                content={
                    <div style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
                        Lưu ý: Khi gửi quyết định này, thông tin công nhận tiến sĩ sẽ được gửi tới nghiên cứu sinh và trạng thái hồ sơ sẽ được chuyển sang <b className="text-success">Công nhận tiến sĩ</b>. Bạn có chắc chắn muốn gửi?
                    </div>
                }
                labelAccept="Đồng ý gửi"
                handleAccept={() => handleSave(true)}
                handleCancel={() => setShowConfirm(false)}
            />
        </div>
    );
};

export default CongNhanTienSiForm;
